// api/onboarding.ts
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { AwsConfigApiGateway } from '@/utils/config/api';
import { getMockBearerToken } from '@/utils/auth/mockAuthSession';

/** Set `REACT_APP_SKIP_ONBOARDING_CHECK=true` in env (e.g. `env/.env.local`) to bypass onboarding API and gates. */
export const isOnboardingGateDisabled = process.env.REACT_APP_SKIP_ONBOARDING_CHECK === 'true';

export interface OnboardingStatus {
  isCompleted: boolean;
  currentStep?: number;
  workspaceName?: string;
  stripeConnected?: boolean; // Deprecated - use stripeConnection.isConnected
  stripeAccountId?: string; // Deprecated - use stripeConnection.stripeAccountId or stripeConnection.accountId
  stripeConnection?: {
    isConnected: boolean;
    stripeAccountId?: string;
    accountId?: string; // OAuth flow (deprecated)
    connectionType?: 'oauth' | 'api_key';
  };
  sdkKey?: string;
}

export interface CompleteOnboardingData {
  workspaceName: string;
  stripeAccountId?: string;
  stripeAuthCode?: string; // From Stripe OAuth
}

export const onboardingApi = {
  async checkStatus(token: string): Promise<OnboardingStatus> {
    if (isOnboardingGateDisabled) {
      return { isCompleted: true };
    }
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/get-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
    });

    if (!response.ok) {
      throw new Error('Failed to check onboarding status');
    }

    const result = await response.json();
    return result.data;
  },

  async saveWorkspace(token: string, workspaceId: string, workspaceName: string): Promise<void> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/rename-workspace`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
      body: JSON.stringify({ id: workspaceId, name: workspaceName }),
    });

    if (!response.ok) {
      throw new Error('Failed to save workspace');
    }
  },

  async connectStripe(
    token: string,
    credentials: { stripePublishableKey: string; stripeSecretKey: string; stripeWebhookSecret: string },
    workspaceId: string,
  ): Promise<{ stripeAccountId: string }> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/connect-stripe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
      body: JSON.stringify({
        stripePublishableKey: credentials.stripePublishableKey,
        stripeSecretKey: credentials.stripeSecretKey,
        stripeWebhookSecret: credentials.stripeWebhookSecret,
        workspaceId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to connect Stripe');
    }

    const result = await response.json();
    return result.data;
  },

  async connectStripeOAuth(
    token: string,
    workspaceId: string,
    authCode: string,
  ): Promise<{ stripeAccountId: string; message: string }> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/connect-stripe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
      body: JSON.stringify({
        workspaceId,
        authCode,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || 'Failed to connect Stripe via OAuth';
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result.data || result;
  },

  async generateSDK(token: string): Promise<{ sdkKey: string }> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/generate-sdk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
    });

    if (!response.ok) {
      throw new Error('Failed to generate SDK');
    }

    const result = await response.json();
    return result.data;
  },

  async createAPICredential(
    token: string,
    name?: string,
  ): Promise<{
    id: string;
    name: string;
    description?: string;
    workspaceId: string;
    siteId: string;
    apiKey: string;
    expiresAt: string | null;
    createdAt: string;
    lastUsed: string;
    createdBy: string;
  }> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/api-credentials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
      body: JSON.stringify({
        name: name || 'Default API Key',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create API credential');
    }

    const result = await response.json();
    return result.data;
  },

  async completeOnboarding(token: string): Promise<void> {
    const response = await fetch(`${AwsConfigApiGateway.pooledTenantApiUrl}/onboarding/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
        Authorization: `Bearer ${token}`,
      } as HeadersInit,
    });

    if (!response.ok) {
      throw new Error('Failed to complete onboarding');
    }
  },
};

// Custom hook for onboarding
// Direct API function that accepts token and workspace ID
export const connectStripeDirect = async (
  token: string,
  workspaceId: string,
  credentials: { stripePublishableKey: string; stripeSecretKey: string; stripeWebhookSecret?: string },
): Promise<{ stripeAccountId: string }> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  // Use authenticated API - the backend will use the Stripe credentials to connect to Stripe
  return await onboardingApi.connectStripe(
    token,
    {
      stripePublishableKey: credentials.stripePublishableKey,
      stripeSecretKey: credentials.stripeSecretKey,
      stripeWebhookSecret: credentials.stripeWebhookSecret || '',
    },
    workspaceId,
  );
};

export const useOnboarding = () => {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);

  // Async function to resolve token from current session, even if user state isn't ready
  const resolveToken = async (): Promise<string | null> => {
    if (user?.signInUserSession?.idToken.jwtToken) {
      return user.signInUserSession.idToken.jwtToken;
    }

    return getMockBearerToken() || null;
  };

  const getWorkspaceId = () => {
    if (!workspace?.id) {
      throw new Error('No workspace selected');
    }
    return workspace.id;
  };

  return {
    checkStatus: async (): Promise<OnboardingStatus> => {
      if (isOnboardingGateDisabled) {
        return { isCompleted: true };
      }
      // Try to resolve token from current session
      const token = await resolveToken();

      if (token) {
        try {
          return await onboardingApi.checkStatus(token);
        } catch (error) {
          console.error('Authenticated status check failed:', error);
          throw new Error('Onboarding status check failed');
        }
      }
      throw new Error('Authentication token could not be resolved from current session');
    },
    saveWorkspace: async (workspaceName: string) => {
      // Resolve token from current session
      const token = await resolveToken();
      const workspaceId = workspace?.id || getWorkspaceId();

      if (!token) {
        throw new Error('Authentication token is required for workspace operations');
      }

      if (!workspaceId) {
        throw new Error('Workspace ID is required for workspace operations');
      }

      return await onboardingApi.saveWorkspace(token, workspaceId, workspaceName);
    },
    connectStripe: async (credentials: {
      stripePublishableKey: string;
      stripeSecretKey: string;
      stripeWebhookSecret?: string;
    }) => {
      // Resolve token from current session
      const token = await resolveToken();
      const workspaceId = workspace?.id || getWorkspaceId();

      if (!token) {
        throw new Error('Authentication token is required for Stripe operations');
      }

      if (!workspaceId) {
        throw new Error('Workspace ID is required for Stripe operations');
      }

      return await onboardingApi.connectStripe(
        token,
        {
          stripePublishableKey: credentials.stripePublishableKey,
          stripeSecretKey: credentials.stripeSecretKey,
          stripeWebhookSecret: credentials.stripeWebhookSecret || '',
        },
        workspaceId,
      );
    },
    connectStripeOAuth: async (authCode: string) => {
      // Resolve token from current session
      const token = await resolveToken();
      const workspaceId = workspace?.id || getWorkspaceId();

      if (!token) {
        throw new Error('Authentication token is required for Stripe operations');
      }

      if (!workspaceId) {
        throw new Error('Workspace ID is required for Stripe operations');
      }

      return await onboardingApi.connectStripeOAuth(token, workspaceId, authCode);
    },
    generateSDK: async () => {
      // Resolve token from current session
      const token = await resolveToken();

      if (!token) {
        throw new Error('Authentication token is required for SDK generation');
      }

      return await onboardingApi.generateSDK(token);
    },
    createAPICredential: async (name?: string) => {
      // Resolve token from current session
      const token = await resolveToken();

      if (!token) {
        throw new Error('Authentication token is required for API credential creation');
      }

      return await onboardingApi.createAPICredential(token, name);
    },
    completeOnboarding: async () => {
      // Resolve token from current session
      const token = await resolveToken();

      if (!token) {
        throw new Error('Authentication token is required for onboarding completion');
      }

      return await onboardingApi.completeOnboarding(token);
    },
  };
};
